import moment from 'moment'
import { TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput/types'
import { buildDateTime } from 'transfix-ui/components/TimeInput/utils/utils'
import { LOAD_STATE, STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { DRIVER_IN_OUT_FORM_STATE } from 'apps/fleet-planner/hooks/useDriverInOutForm'
import { IStop } from 'apps/fleet-planner/types'
import { CHECK_IN_STATES } from 'apps/fleet-planner/utils/driverStatuses'

interface IInOutTimesLabels {
  checkType: string
  message: string
  header: string
}

interface ICreateLabelsArgs {
  isMulti: boolean
  stopType: string
  currentStopNumber: string
  isCheckIn?: boolean
  nextStatus?: LOAD_STATE
}

const createLabels = ({
  currentStopNumber,
  isCheckIn,
  isMulti,
  nextStatus,
  stopType,
}: ICreateLabelsArgs): IInOutTimesLabels => {
  if (nextStatus === LOAD_STATE.Dispatched) {
    return {
      checkType: 'Pickup ETA',
      message: 'Please provide your ETA to the pickup',
      header: 'Confirm your ETA',
    }
  }
  const arriveMessage = isMulti
    ? `When did you arrive at ${stopType}${currentStopNumber}?`
    : `When did you arrive at the ${stopType}?`
  const leaveMessage = isMulti
    ? `When did you leave ${stopType}${currentStopNumber}?`
    : `When did you leave the ${stopType}?`
  const message = isCheckIn ? arriveMessage : leaveMessage
  const checkType = isCheckIn ? 'Check-In' : 'Check-Out'
  const header = `Submit your ${checkType} time`
  return { checkType, message, header }
}

interface IGetLabelArgs {
  state: DRIVER_IN_OUT_FORM_STATE
  isMulti: boolean
  currentStopNumber: string
  nextStatus: LOAD_STATE
  currentStop?: IStop
}

export const getLabels = ({
  currentStop,
  currentStopNumber,
  isMulti,
  nextStatus,
  state,
}: IGetLabelArgs): IInOutTimesLabels => {
  const { stopType = '' } = currentStop || {}
  if (state === DRIVER_IN_OUT_FORM_STATE.checkedInAt) {
    const { checkType, message } = createLabels({
      isMulti,
      stopType,
      currentStopNumber,
      isCheckIn: true,
    })
    return {
      checkType,
      message,
      header: `Edit ${checkType.toLowerCase()} time`,
    }
  }
  if (state === DRIVER_IN_OUT_FORM_STATE.checkedOutAt) {
    const { checkType, message } = createLabels({
      isMulti,
      stopType,
      currentStopNumber,
      isCheckIn: false,
    })
    return {
      checkType,
      message,
      header: `Edit ${checkType.toLowerCase()} time`,
    }
  }
  // Default return value for DRIVER_IN_OUT_FORM_STATE.statusUpdate
  const { checkType = '', message = '' } = createLabels({
    isMulti,
    stopType,
    currentStopNumber,
    isCheckIn: CHECK_IN_STATES.includes(nextStatus),
    nextStatus,
  })
  return {
    checkType,
    message,
    header:
      nextStatus === LOAD_STATE.Dispatched
        ? 'Confirm your ETA'
        : `Submit your ${checkType.toLowerCase()} time`,
  }
}

export const getInitialTime = (state: DRIVER_IN_OUT_FORM_STATE, stop?: IStop): string => {
  const { checkedInAt, checkedOutAt, startAt } = stop || {}
  if (state === DRIVER_IN_OUT_FORM_STATE.checkedInAt) {
    return checkedInAt || ''
  }
  if (state === DRIVER_IN_OUT_FORM_STATE.checkedOutAt) {
    return checkedOutAt || ''
  }
  return startAt || ''
}

interface IShouldPromptForTrailerNumber {
  nextStatus: LOAD_STATE
  stops: IStop[]
  isExternal: boolean
}

export const shouldPromptForTrailerNumber = ({
  isExternal,
  nextStatus,
  stops,
}: IShouldPromptForTrailerNumber) => {
  if (!isExternal) {
    return false
  }

  const isDropAtPickup = stops.some(
    (stop: IStop) =>
      stop.isDropTrailer && stop.stopType === STOP_TYPE.pickup && stop.stopSequence === 1
  )
  const isDropAtDropOff = stops.some(
    (stop: IStop) => stop.isDropTrailer && stop.stopType === STOP_TYPE.dropoff
  )
  if (isDropAtPickup && nextStatus === LOAD_STATE['In Transit']) {
    return true
  }
  if (isDropAtDropOff && !isDropAtPickup && nextStatus === LOAD_STATE.Dispatched) {
    return true
  }
  return false
}

interface IShouldCheckLateArrival {
  isExternal: boolean
  formState: DRIVER_IN_OUT_FORM_STATE
  startAt: string | undefined
  checkType: string
}

export const shouldCheckLateArrival = ({
  checkType,
  formState,
  isExternal,
  startAt,
}: IShouldCheckLateArrival): boolean => {
  return (
    isExternal &&
    formState === DRIVER_IN_OUT_FORM_STATE.statusUpdate &&
    !!startAt &&
    checkType === 'Check-In'
  )
}

interface ICheckLateArrival {
  time: string
  date: string
  initialTZ: string
  endAt: string | undefined
  startAt: string | undefined
}

export const checkLateArrival = ({
  date,
  endAt,
  initialTZ,
  startAt,
  time,
}: ICheckLateArrival): boolean => {
  const timestamp = buildDateTime(date, time, TIME_INPUT_FORMAT.MILITARY_SHORT, initialTZ)
  const lateAt = endAt || startAt
  if (timestamp?.isValid) {
    return timestamp > moment(lateAt)
  }
  return false
}
